import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, Clock, ArrowRight } from 'lucide-react';

const LOGO_URL = "/images/logo.png";

const Footer = () => {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Pricing', path: '/service-offer-plan' },
    { name: 'Book Now', path: '/booking' },
    { name: 'Gallery', path: '/gallery' },
  ];

  const companyLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Founder\'s Desk', path: '/ceo-desk' },
    { name: 'Vision & Mission', path: '/vision-mission' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact', path: '/contact' },
  ];

  const supportLinks = [
    { name: 'How It Works', path: '/how-it-works' },
    { name: 'Why Choose Us', path: '/why-choose' },
    { name: 'Who We Serve', path: '/who-we-serve' },
    { name: 'FAQ', path: '/faq' },
  ];

  return (
    <footer className="bg-[#0B2C5D] text-white" data-testid="footer">
      {/* CTA Section */}
      <div className="bg-gradient-to-r from-[#1E88E5] to-[#0B2C5D] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                Ready for a Premium Car Wash?
              </h3>
              <p className="text-white/80">
                Book your doorstep service now and experience the difference.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/booking"
                className="bg-[#F7931E] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg flex items-center justify-center"
                data-testid="footer-book-now"
              >
                Book Now <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a
                href="https://wa.me/919044499957?text=Hi%20Washora,%20I'd%20like%20to%20book%20a%20car%20wash%20service."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-[#0B2C5D] px-8 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all flex items-center justify-center"
                data-testid="footer-whatsapp"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
            {/* Brand Column */}
            <div className="lg:col-span-2">
              <Link to="/" className="inline-block mb-6">
                <img src={LOGO_URL} alt="Washora Premium Care" className="h-16 w-auto" />
              </Link>
              <p className="text-white/70 mb-6 leading-relaxed">
                Core Karnataka's First Doorstep Car Wash, beyond the capital. We bring premium car care services directly to your doorstep.
              </p>
              <div className="space-y-3">
                <a 
                  href="tel:9044499957" 
                  className="flex items-center text-white/80 hover:text-[#F7931E] transition-colors"
                >
                  <Phone className="h-5 w-5 mr-3 text-[#1E88E5]" />
                  9044499957
                </a>
                <a 
                  href="mailto:info@washora.org" 
                  className="flex items-center text-white/80 hover:text-[#F7931E] transition-colors"
                >
                  <Mail className="h-5 w-5 mr-3 text-[#1E88E5]" />
                  info@washora.org
                </a>
                <div className="flex items-start text-white/80">
                  <MapPin className="h-5 w-5 mr-3 text-[#1E88E5] flex-shrink-0 mt-0.5" />
                  <span>#3/21, 3rd Cross, Near Park, Basaweshwara Nagar, Chitradurga - 577502</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading text-lg font-semibold mb-6">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link 
                      to={link.path} 
                      className="text-white/70 hover:text-[#F7931E] transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-heading text-lg font-semibold mb-6">Company</h4>
              <ul className="space-y-3">
                {companyLinks.map((link) => (
                  <li key={link.path}>
                    <Link 
                      to={link.path} 
                      className="text-white/70 hover:text-[#F7931E] transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-heading text-lg font-semibold mb-6">Support</h4>
              <ul className="space-y-3">
                {supportLinks.map((link) => (
                  <li key={link.path}>
                    <Link 
                      to={link.path} 
                      className="text-white/70 hover:text-[#F7931E] transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <a
                  href="https://instagram.com/washora_premiumcare"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-white/70 hover:text-[#F7931E] transition-colors"
                  data-testid="footer-instagram"
                >
                  <Instagram className="h-5 w-5 mr-2" />
                  @washora_premiumcare
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-white/60 text-sm">
                © {new Date().getFullYear()} Washora Premium Care. All rights reserved.
              </p>
              <p className="text-white/60 text-sm mt-1">
                Made with <span className="text-red-500">❤️</span> by{' '}
                <a 
                  href="https://trivarta.in" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#F7931E] hover:text-white transition-colors"
                >
                  Trivarta Tech Pvt Ltd
                </a>
              </p>
            </div>
            <div className="flex items-center text-white/60 text-sm">
              <Clock className="h-4 w-4 mr-2" />
              Service Hours: 7:00 AM - 7:00 PM (All Days)
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
